import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, BookOpen, Loader2, Layers, Settings2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { priceBookItemsApi, priceBookAssembliesApi, pbCategoriesApi } from '../api';
import { centsToDollarString } from '../constants';
import { ITEM_SORT_OPTIONS, ASSEMBLY_SORT_OPTIONS, PRICING_STATUS_STYLES, pricingStatusLabel, unitAbbr, COST_CLASS_OPTIONS } from '../priceBookConstants';
import { ItemFormDialog } from '../components/ItemFormDialog';
import { AssemblyFormDialog } from '../components/AssemblyFormDialog';
import { CategoryManagerDialog } from '../components/CategoryManagerDialog';

const selectCls = 'bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';
const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';
const UNCAT = '__uncategorized__';

const price = (cents, item) => (cents === null || cents === undefined ? 'Not set' : `${centsToDollarString(cents)} / ${unitAbbr(item)}`);

const StatusBadge = ({ status }) => (
  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${PRICING_STATUS_STYLES[status] || PRICING_STATUS_STYLES.missing_standard_price}`}>{pricingStatusLabel(status)}</span>
);

const CategoryRail = ({ categories, counts, uncatCount, totalCount, selected, onSelect }) => {
  const entry = (key, label, count, testId) => (
    <button
      key={key}
      onClick={() => onSelect(key)}
      className={`flex items-center justify-between gap-2 w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${
        selected === key ? 'bg-[#D4AF37]/15 text-[#D4AF37] font-medium' : 'text-gray-400 hover:text-white hover:bg-[#1A1A1A]'
      }`}
      data-testid={testId}
    >
      <span className="truncate">{label}</span>
      <span className={`text-[11px] shrink-0 ${selected === key ? 'text-[#D4AF37]/70' : 'text-gray-600'}`}>{count}</span>
    </button>
  );
  return (
    <div className="space-y-0.5" data-testid="pb-category-rail">
      {entry('', 'All Categories', totalCount, 'pb-cat-all')}
      {categories.map((c) => entry(c.name, c.name, counts[c.name] || 0, `pb-cat-${c.id}`))}
      {uncatCount > 0 && entry(UNCAT, 'Uncategorized', uncatCount, 'pb-cat-uncategorized')}
    </div>
  );
};

const ItemRow = ({ it, onClick }) => (
  <button onClick={onClick}
    className={`w-full flex flex-wrap items-center gap-x-4 gap-y-1 bg-[#111111] hover:bg-[#1A1A1A] border border-gray-800 rounded-lg px-4 py-3 text-left transition-colors ${it.is_archived ? 'opacity-60' : ''}`}
    data-testid={`pb-item-row-${it.id}`}>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-white font-medium">{it.name}</span>
        {it.item_code && <span className="text-[10px] text-gray-500 border border-gray-700 rounded px-1.5 py-0.5">{it.item_code}</span>}
        <StatusBadge status={it.pricing_status} />
      </div>
      <div className="text-xs text-gray-500 mt-0.5 truncate">
        {it.category || 'Uncategorized'}{it.cost_classification ? ` · ${COST_CLASS_OPTIONS.find((o) => o.value === it.cost_classification)?.label}` : ''}
      </div>
    </div>
    <div className="text-xs text-gray-400 w-32 text-right shrink-0">Price <span className="text-gray-200 block">{price(it.standard_unit_price_cents, it)}</span></div>
  </button>
);

const AsmRow = ({ a, onClick, preview }) => (
  <button onClick={onClick}
    className={`w-full flex flex-wrap items-center gap-x-4 gap-y-1 bg-[#111111] hover:bg-[#1A1A1A] border border-gray-800 rounded-lg px-4 py-3 text-left transition-colors ${a.is_archived ? 'opacity-60' : ''}`}
    data-testid={`pb-asm-row-${a.id}`}>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-white font-medium">{a.name}</span>
        {a.preview.pricing_incomplete && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-amber-900/30 text-amber-400 border-amber-800">Pricing Incomplete</span>}
        {a.preview.is_empty && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-gray-800 text-gray-400 border-gray-700">Empty</span>}
      </div>
      <div className="text-xs text-gray-500 mt-0.5 truncate">{a.category || 'Uncategorized'} · {a.preview.component_count} component{a.preview.component_count === 1 ? '' : 's'}</div>
    </div>
    <div className="text-xs text-gray-400 w-28 text-right shrink-0">Price <span className="text-gray-200 block">{preview}</span></div>
  </button>
);

export default function OpsPriceBook() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'assemblies' ? 'assemblies' : 'items';
  const [items, setItems] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCat, setSelectedCat] = useState('');
  const [f, setF] = useState({ search: '', archived: false, sort: 'name_az' });
  const [itemDialog, setItemDialog] = useState({ open: false, item: null });
  const [asmDialog, setAsmDialog] = useState({ open: false, assembly: null });
  const [catManagerOpen, setCatManagerOpen] = useState(false);

  const loadCategories = useCallback(() => {
    pbCategoriesApi.list().then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const loadDocs = useCallback(() => {
    setLoading(true);
    const p = { archived: f.archived, sort: f.sort };
    if (f.search.trim()) p.search = f.search.trim();
    const api = tab === 'items' ? priceBookItemsApi : priceBookAssembliesApi;
    api.list(p)
      .then((r) => (tab === 'items' ? setItems(r.data) : setAssemblies(r.data)))
      .catch(() => toast.error('Could not load Price Book'))
      .finally(() => setLoading(false));
  }, [tab, f]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  const refresh = () => { loadCategories(); loadDocs(); };

  const asmPreview = (a) =>
    a.preview.missing_standard_price_component_count > 0
      ? <span className="text-amber-400">Incomplete</span>
      : centsToDollarString(a.preview.standard_price_preview_cents);

  const docs = tab === 'items' ? items : assemblies;
  const catNames = new Set(categories.map((c) => c.name));
  const counts = {};
  let uncatCount = 0;
  docs.forEach((d) => {
    if (d.category && catNames.has(d.category)) counts[d.category] = (counts[d.category] || 0) + 1;
    else uncatCount += 1;
  });

  const visible = selectedCat === ''
    ? docs
    : selectedCat === UNCAT
      ? docs.filter((d) => !d.category || !catNames.has(d.category))
      : docs.filter((d) => d.category === selectedCat);

  // Grouped view for "All Categories"
  const groups = [];
  if (selectedCat === '') {
    categories.forEach((c) => {
      const inCat = docs.filter((d) => d.category === c.name);
      if (inCat.length) groups.push({ key: c.id, label: c.name, docs: inCat });
    });
    const uncat = docs.filter((d) => !d.category || !catNames.has(d.category));
    if (uncat.length) groups.push({ key: UNCAT, label: 'Uncategorized', docs: uncat });
  }

  const renderDoc = (d) =>
    tab === 'items'
      ? <ItemRow key={d.id} it={d} onClick={() => setItemDialog({ open: true, item: d })} />
      : <AsmRow key={d.id} a={d} preview={asmPreview(d)} onClick={() => setAsmDialog({ open: true, assembly: d })} />;

  const emptyState = (
    <Card className="bg-[#111111] border-gray-800 p-12 text-center" data-testid={tab === 'items' ? 'pb-items-empty' : 'pb-asm-empty'}>
      {tab === 'items' ? <BookOpen size={32} className="mx-auto mb-3 text-gray-700" /> : <Layers size={32} className="mx-auto mb-3 text-gray-700" />}
      <p className="text-gray-500">
        {f.search.trim() || selectedCat
          ? 'Nothing here yet. Try another category or clear the search.'
          : tab === 'items' ? 'No Price Book items yet. Create your first reusable line item.' : 'No assemblies yet. Group items into reusable packages.'}
      </p>
    </Card>
  );

  return (
    <div data-testid="ops-price-book">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-white">Price Book</h2>
          <p className="text-gray-400 mt-1">Your pricing library, organized by category.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCatManagerOpen(true)} className="border-gray-700 text-gray-300" data-testid="manage-categories-btn">
            <Settings2 size={15} /> Manage Categories
          </Button>
          <Button
            onClick={() => (tab === 'items' ? setItemDialog({ open: true, item: null }) : setAsmDialog({ open: true, assembly: null }))}
            className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold"
            data-testid={tab === 'items' ? 'new-item-btn' : 'new-assembly-btn'}
          >
            <Plus size={16} /> {tab === 'items' ? 'New Item' : 'New Assembly'}
          </Button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-gray-800 mb-5 pb-px">
        {[{ k: 'items', l: 'Items' }, { k: 'assemblies', l: 'Assemblies' }].map((t) => (
          <button key={t.k} onClick={() => { setSearchParams(t.k === 'items' ? {} : { tab: t.k }); }}
            className={`px-4 py-2 text-sm border-b-2 transition-colors ${tab === t.k ? 'border-[#D4AF37] text-[#D4AF37] font-medium' : 'border-transparent text-gray-400 hover:text-white'}`}
            data-testid={`pb-tab-${t.k}`}>
            {t.l}
          </button>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Category rail */}
        <aside className="lg:w-56 shrink-0">
          <div className="lg:sticky lg:top-4 max-h-none lg:max-h-[calc(100vh-6rem)] overflow-x-auto lg:overflow-y-auto">
            <div className="hidden lg:block">
              <CategoryRail categories={categories} counts={counts} uncatCount={uncatCount} totalCount={docs.length} selected={selectedCat} onSelect={setSelectedCat} />
            </div>
            {/* Mobile: horizontal chips */}
            <div className="flex lg:hidden gap-2 overflow-x-auto pb-2" data-testid="pb-category-chips">
              {[{ key: '', label: `All (${docs.length})` },
                ...categories.filter((c) => counts[c.name]).map((c) => ({ key: c.name, label: `${c.name} (${counts[c.name]})` })),
                ...(uncatCount ? [{ key: UNCAT, label: `Uncategorized (${uncatCount})` }] : [])].map((c) => (
                <button key={c.key || 'all'} onClick={() => setSelectedCat(c.key)}
                  className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap border transition-colors ${
                    selectedCat === c.key ? 'bg-[#D4AF37] text-black border-[#D4AF37] font-semibold' : 'border-gray-700 text-gray-400 hover:text-white'}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[180px] max-w-sm">
              <Search size={14} className="absolute left-3 top-3 text-gray-500" />
              <Input value={f.search} onChange={(e) => setF((x) => ({ ...x, search: e.target.value }))} placeholder={`Search ${tab}…`} className={`${inputCls} pl-8 w-full`} data-testid={tab === 'items' ? 'pb-item-search' : 'pb-asm-search'} />
            </div>
            <select value={f.sort} onChange={(e) => setF((x) => ({ ...x, sort: e.target.value }))} className={selectCls} data-testid="pb-sort">
              {(tab === 'items' ? ITEM_SORT_OPTIONS : ASSEMBLY_SORT_OPTIONS).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-gray-400">
              <input type="checkbox" checked={f.archived} onChange={(e) => setF((x) => ({ ...x, archived: e.target.checked }))} data-testid="pb-archived-toggle" /> Archived
            </label>
          </div>

          {loading ? (
            <div className="text-center py-16 text-gray-500"><Loader2 size={26} className="mx-auto mb-3 animate-spin text-[#D4AF37]" /> Loading…</div>
          ) : visible.length === 0 ? (
            emptyState
          ) : selectedCat === '' ? (
            <div className="space-y-6" data-testid={tab === 'items' ? 'pb-items-list' : 'pb-asm-list'}>
              {groups.map((g) => (
                <div key={g.key} data-testid={`pb-group-${g.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-xs font-semibold text-[#D4AF37] uppercase tracking-widest">{g.label}</h4>
                    <span className="text-[11px] text-gray-600">{g.docs.length}</span>
                    <div className="h-px bg-gray-800 flex-1" />
                  </div>
                  <div className="space-y-2">{g.docs.map(renderDoc)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2" data-testid={tab === 'items' ? 'pb-items-list' : 'pb-asm-list'}>
              {visible.map(renderDoc)}
            </div>
          )}
        </div>
      </div>

      <ItemFormDialog open={itemDialog.open} onOpenChange={(o) => setItemDialog((x) => ({ ...x, open: o }))} item={itemDialog.item} onSaved={refresh} defaultCategory={selectedCat && selectedCat !== UNCAT ? selectedCat : ''} />
      <AssemblyFormDialog open={asmDialog.open} onOpenChange={(o) => setAsmDialog((x) => ({ ...x, open: o }))} assembly={asmDialog.assembly} onSaved={refresh} defaultCategory={selectedCat && selectedCat !== UNCAT ? selectedCat : ''} />
      <CategoryManagerDialog open={catManagerOpen} onOpenChange={setCatManagerOpen} onChanged={refresh} />
    </div>
  );
}
